import { createElement } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Flex, Image } from '@mantine/core'
import { IconArrowDown, IconArrowUp } from '@tabler/icons-react'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import { isBudgetCategory } from '@/types/budgets.types'

import type { SupportLocationState } from '@/modules/support/routes/supportRouteConfig.types'
import { SupportRoutePathsEnum } from '@/modules/support/routes/supportRouteConfig.types'

import { MoreDropdown } from '@/components/MoreDropdown/MoreDropdown'
import { Text } from '@/components/Text/Text'
import { Tooltip } from '@/components/Tooltip/Tooltip'
import type { TransactionsListItemProps } from '@/components/TransactionsListItem/TransactionsListItem.types'

import { TransactionType } from '@/constants/transactions.constants'

import {
  useBankingServiceBankingControllerGetIncomeStatsKey,
  useBankingServiceBankingControllerGetSpendingStatsKey,
  useBudgetServiceBudgetControllerGetAllBudgetsKey,
  useTransactionServiceTransactionControllerGetTransactionsKey,
  useTransactionServiceTransactionControllerToggleExcluded,
} from '@/__generated__/queries'
import { category as BudgetCategory } from '@/__generated__/requests'

import { INSIGHTS_QUERY_KEY } from '@/hooks/useInsightsQuery.hook'

import { capitalizeFirstLetter } from '@/helpers/capitalizeFirstLetter.helper'
import { formatMoney } from '@/helpers/formatMoney.helper'
import { getBudgetCategoryIcon } from '@/helpers/getBudgetCategoryIcon.helper'
import { mapCategories } from '@/helpers/mapCategories.helper'

import classes from './TransactionsListItem.module.css'

export function TransactionsListItem({
  onEdit,
  transaction,
  type,
}: TransactionsListItemProps) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const INVALIDATE_KEYS = [
    useBudgetServiceBudgetControllerGetAllBudgetsKey,
    useTransactionServiceTransactionControllerGetTransactionsKey,
    useBankingServiceBankingControllerGetIncomeStatsKey,
    useBankingServiceBankingControllerGetSpendingStatsKey,
    INSIGHTS_QUERY_KEY,
  ]

  const toggleExcludeMutation =
    useTransactionServiceTransactionControllerToggleExcluded({
      onSuccess: () => {
        INVALIDATE_KEYS.forEach(key => {
          queryClient.invalidateQueries({
            queryKey: [key],
          })
        })
      },
    })

  let transactionCategoryIcon = null

  const defaultCategoryIcon = getBudgetCategoryIcon(BudgetCategory.OTHER)

  if (isBudgetCategory(transaction.category)) {
    transactionCategoryIcon = getBudgetCategoryIcon(transaction.category)
  }

  const transactionAmount =
    type === TransactionType.INCOME
      ? Math.abs(transaction.amount)
      : -transaction.amount

  const handleToggleExcluded = () => {
    toggleExcludeMutation.mutate({
      transactionId: transaction.id,
    })
  }

  const handleReport = () => {
    const state: SupportLocationState = {
      supportReference: {
        id: transaction.id,
        label: transaction.merchant || transaction.name,
        secondary: transaction.accountName
          ? `${transaction.accountName} ****${transaction.accountMask}`
          : null,
        type: 'transaction',
      },
    }

    navigate(SupportRoutePathsEnum.SUPPORT, { state })
  }

  const MORE_ITEMS = [
    {
      label: transaction.excluded ? 'Include' : 'Exclude',
      onClick: handleToggleExcluded,
    },
    {
      label: 'Edit',
      onClick: () => onEdit(transaction),
    },
    {
      label: 'Report',
      onClick: handleReport,
    },
  ]

  return (
    <Flex
      className={
        transaction.excluded ? classes.excludedContainer : classes.container
      }
    >
      <Flex align="center" flex={1} gap={16}>
        <Box className={classes.icon}>
          {transaction.logo && (
            <Image h={48} radius={8} src={transaction.logo} w={48} />
          )}

          {transactionCategoryIcon && !transaction.logo
            ? createElement(transactionCategoryIcon)
            : createElement(defaultCategoryIcon)}
        </Box>

        <Flex
          align={{
            base: 'flex-start',
            lg: 'flex-start',
            sm: 'center',
            xl: 'center',
          }}
          direction={{
            base: 'column',
            lg: 'column',
            sm: 'row',
            xl: 'row',
          }}
          flex={1}
          gap={{
            base: 0,
            lg: 0,
            sm: 8,
            xl: 16,
          }}
        >
          <Text
            lineClamp={2}
            variant="body_2_medium_black"
            w={{
              base: 'auto',
              lg: 'auto',
              md: '200px',
              sm: '160px',
              xl: '40%',
            }}
          >
            {transaction.merchant || transaction.name}
          </Text>

          <Text
            mr={8}
            variant="body_2_medium_black"
            w={{
              base: 'auto',
              sm: 126,
              xl: '25%',
            }}
          >
            {transaction.datetime
              ? dayjs(transaction.datetime).format('MMM DD, h:mm A')
              : `${dayjs(transaction.date).format('MMM DD')}, N/A`}
          </Text>

          <Text
            pos="relative"
            variant="body_2_medium_black"
            w={{ base: 'auto', xl: '25%' }}
          >
            {isBudgetCategory(transaction.category)
              ? mapCategories(transaction.category)
              : capitalizeFirstLetter(transaction.category)}
          </Text>
        </Flex>
      </Flex>

      <Flex>
        <Flex
          align="center"
          gap={4}
          justify="flex-end"
          mr={8}
          w={{
            base: 'auto',
            sm: 140,
          }}
        >
          {type === TransactionType.SPENDING &&
            (transactionAmount < 0 ? (
              <IconArrowDown
                color="var(--mantine-other-colors-red)"
                size={16}
              />
            ) : (
              <IconArrowUp
                color="var(--mantine-other-colors-green)"
                size={16}
              />
            ))}

          <Text>{formatMoney(transactionAmount, 'USD')}</Text>
        </Flex>

        <Tooltip
          isInfo
          label={`${transaction.accountName} **** **** **** ${transaction.accountMask}`}
        />

        <MoreDropdown items={MORE_ITEMS} />
      </Flex>
    </Flex>
  )
}
